import { useRef } from 'react';
import { useForm } from 'react-hook-form';
import { Button, Form } from '@qodo/design-system';
import { zodResolver } from '@hookform/resolvers/zod';
import { SESSION_STORAGE_KEYS } from 'consts/sessionStorage';
import {
  useAdoptDeviceCodeMutation,
  useConfirmDeviceCodeMutation,
} from 'network/mutations';
import { mixpanelService } from 'services/mixpanel';
import {
  MIXPANEL_EVENT_NAMES,
  MIXPANEL_OPERATIONS,
} from 'services/mixpanel/consts';
import { spawn } from 'utils/translations';
import {
  clearCarriedCliContext,
  getSessionIdFromSessionStorage,
} from 'utils/url';
import { FIELDS } from './consts';
import { getFormSchema } from './schemas';
import { DeviceCodeVerifyProps, FormData } from './types';
import { normalizeDeviceCode } from './utils';

/**
 * Device-code confirmation for CLI logins (RFC 8628 / GitHub model): the user manually types
 * the code shown in their terminal. Typing the code IS the possession proof + anti-phishing
 * confirmation (RFC 8628 §5.4), so there is no prefill, no auto-adopt, and no adoption cookie
 * — the code never travels except terminal → user's eyes → this form, which is what closes
 * the login-CSRF structurally (a crafted URL just lands the victim on a form asking for a code
 * they don't have).
 *
 * Two deployments, one form:
 * - MT (individual / non-dedicated): the MT device session already exists — its session_id
 *   was carried from the CLI to this page — so we confirm it directly.
 * - ST (dedicated-org, post MT→ST redirect): landed on a fresh origin with NO session_id, so
 *   we adopt-then-confirm the typed code (initLogin(adopt_user_code) creates the ST session
 *   keyed by the code, then confirm mints). `useAdoptDeviceCodeMutation` performs that sequence.
 *
 * Routing (host-agnostic — dedicated portals aren't always `*.st.*`, e.g. `acme-corp.qodo.ai`,
 * so a hostname check would misroute them). We distinguish the two by WHO wrote the session_id:
 * - MT device session: a session_id carried from the CLI, with NO adopt marker.
 * - ST-adopted session: written by useAdoptDeviceCodeMutation, which always also sets the
 *   USER_CODE marker (the code it adopted for).
 * So "MT" = a session_id present at mount AND no adopt marker. Anything else (no session, or a
 * session with an adopt marker — e.g. a reload mid-ST-login) goes through the adopt mutation,
 * which refreshes the (possibly-expired) Bearer and is idempotent for the same code. Captured
 * at mount so the adopt mutation persisting its own session_id can't flip the path on a retry.
 */
export const DeviceCodeVerify = ({ onConfirmed }: DeviceCodeVerifyProps) => {
  const spawnText = spawn('device-code');
  const { mutateAsync: confirmDeviceCode, isPending: isConfirming } =
    useConfirmDeviceCodeMutation();
  const { mutateAsync: adoptDeviceCode, isPending: isAdopting } =
    useAdoptDeviceCodeMutation();

  // A genuine MT device session: session_id present at mount with no ST-adopt marker. An
  // ST-adopted session (even after a reload) carries the USER_CODE marker, so it must NOT take
  // the direct-confirm path (which skips the Bearer refresh the ST needs).
  const mtSessionIdRef = useRef<string | undefined>(
    (() => {
      const sessionId = getSessionIdFromSessionStorage();
      let hasAdoptMarker = false;

      try {
        hasAdoptMarker = !!sessionStorage.getItem(
          SESSION_STORAGE_KEYS.USER_CODE,
        );
      } catch {
        hasAdoptMarker = false;
      }

      return sessionId && !hasAdoptMarker ? sessionId : undefined;
    })(),
  );

  const methods = useForm<FormData>({
    resolver: zodResolver(getFormSchema()),
    defaultValues: { [FIELDS.CODE]: '' },
  });

  const onSubmit = async (data: FormData) => {
    // Backend stores codes as uppercase `XXXX-XXXX` and compares hyphen-sensitively, so
    // normalize to that exact shape: uppercase and insert the hyphen if the user omitted it.
    const userCode = normalizeDeviceCode(data[FIELDS.CODE]);

    const mtSessionId = mtSessionIdRef.current;

    // mt = confirm an existing MT device session; st = adopt-then-confirm on a
    // dedicated-org landing. Carried on the event so we can see which path
    // validates (and fails) in analytics. The base props (init_client, trace_id,
    // login_session_id) are auto-attached by mixpanelService, so this is the
    // same login funnel the CLI/IDE emit into — no extra correlation wiring.
    const deployment = mtSessionId ? 'mt' : 'st';

    try {
      if (mtSessionId) {
        // MT: confirm the existing device session (its id_token is already fresh).
        await confirmDeviceCode({ sessionId: mtSessionId, userCode });
      } else {
        // ST landing (or reload mid-ST-login): adopt-then-confirm — retry-safe (reuses an
        // already-adopted ST session for the same code, re-adopts a corrected one) and always
        // refreshes the Bearer, so an expired migrated token doesn't dead-end the confirm.
        await adoptDeviceCode({ userCode });
      }

      mixpanelService.track(MIXPANEL_EVENT_NAMES.SYSTEM, {
        operation: MIXPANEL_OPERATIONS.DEVICE_CODE_VALIDATION_SUCCESS,
        interaction_details: { device_flow_deployment: deployment },
      });

      clearCarriedCliContext();
      onConfirmed();
    } catch {
      mixpanelService.track(MIXPANEL_EVENT_NAMES.ERROR, {
        operation: MIXPANEL_OPERATIONS.DEVICE_CODE_VALIDATION_FAILED,
        interaction_details: { device_flow_deployment: deployment },
      });
      methods.setError('root', { message: spawnText('error') });
    }
  };

  const isPending = isConfirming || isAdopting;

  return (
    <Form
      methods={methods}
      onSubmit={onSubmit}
      className="items-center text-center"
    >
      <h2 className="text-purple-muted-100">{spawnText('title')}</h2>
      <p className="text-purple-muted-300">{spawnText('description')}</p>
      <Form.Input
        autoFocus
        name={FIELDS.CODE}
        aria-label={spawnText('label')}
        placeholder={spawnText('placeholder')}
        // The device code is an auth secret; keep it out of Sentry session replay.
        data-sentry-mask="true"
        className="text-center uppercase tracking-widest"
      />
      {methods.formState.errors.root && (
        <p className="text-sm text-destructive">
          {methods.formState.errors.root.message}
        </p>
      )}
      <Button type="submit" loading={isPending} disabled={isPending}>
        {spawnText('submit')}
      </Button>
    </Form>
  );
};
